import {
	action,
	observable,
	makeObservable,
	computed,
	runInAction,
	reaction,
} from 'mobx';
import {enumToValues} from '@netx/core-next/utils/enum';
import {assetExpirationEnabled} from '@netx/core-asset-expiration/enablers';
import {RuleModel} from '../baseModel';
import {
	DateSubType,
	DateType,
	DateNoValueTypes,
	SystemDefined,
	SearchTypeDate,
	TSearchTypeDate,
} from './constants';
import {dateRuleDefaults} from './defaults';
import type {DateRule} from './types';
import {validateDateRule} from './validate';

export class DateRuleModel
	extends RuleModel<TSearchTypeDate>
	implements
		Omit<DateRule, 'elementValue1' | 'elementValue2' | 'elementValue3'>
{
	readonly elementType = SearchTypeDate;
	elementSubType1: DateType = dateRuleDefaults.elementSubType1;
	elementSubType2: DateSubType = dateRuleDefaults.elementSubType2;
	elementValue1: string | '' = dateRuleDefaults.elementValue1;
	elementValue2: string | '' = dateRuleDefaults.elementValue2;
	elementValue3: string | '' = dateRuleDefaults.elementValue3;

	get elementSubType1Options() {
		const enabled = assetExpirationEnabled();
		return enabled
			? enumToValues(DateType)
			: enumToValues(DateType).filter(
					(type) => type !== DateType.EXPIRATION_DATE,
			  );
	}

	get elementSubType2Options() {
		return enumToValues(DateSubType);
	}

	get isValid() {
		const validation = validateDateRule(this as DateRule);
		this.setValidationErrors(this.dirty && validation ? validation : undefined);
		if (!validation) return true;
		return false;
	}

	constructor(params?: Partial<Omit<DateRule, 'elementType'>>) {
		super();

		runInAction(() => {
			Object.assign(this, {...dateRuleDefaults, ...params});
		});

		makeObservable(this, {
			elementSubType1: observable,
			elementSubType1Options: computed,
			elementSubType2: observable,
			elementSubType2Options: computed,
			elementValue1: observable,
			elementValue2: observable,
			elementValue3: observable,
			isValid: computed,
			setElementSubType1: action,
			setElementSubType2: action,
			setElementValue1: action,
			setElementValue2: action,
			setElementValue3: action,
		});

		this.reactionDisposers.push(
			reaction(
				() => this.elementSubType1,
				(elementSubType1) => {
					// no value3 if non user defined attribute
					if (elementSubType1 !== DateType.USER_DEFINED_ATTRIBUTE) {
						this.elementValue3 = '';
					}
					if (this.elementValue1 === '' && this.elementValue2 === '') {
						this.dirty = false;
					}
				},
			),
			reaction(
				() => this.elementSubType2,
				(elementSubType2) => {
					// no value2 if not `in_between`
					if (DateNoValueTypes.includes(elementSubType2 as any)) {
						this.elementValue1 = '';
						this.elementValue2 = '';
						this.dirty = false;
					} else if (elementSubType2 !== DateSubType.IN_BETWEEN) {
						this.elementValue2 = '';
						if (this.elementValue1 === '') {
							this.dirty = false;
						}
					}
				},
			),
		);
	}

	setElementSubType1(elementSubType1: DateType) {
		this.elementSubType1 = elementSubType1;
	}

	setElementSubType2(elementSubType2: DateSubType) {
		this.elementSubType2 = elementSubType2;
	}

	/**
	 * @param elementValue1 Start date if `in_between`, selected date if `before` or `after`, otherwise stubbed as `""`
	 */
	setElementValue1<T2 extends DateSubType = DateSubType>(
		elementValue1: [T2] extends [
			DateSubType.AFTER | DateSubType.BEFORE | DateSubType.IN_BETWEEN,
		]
			? string
			: '',
	) {
		this.dirty = this.elementValue1 !== elementValue1;
		this.elementValue1 = elementValue1 as this['elementValue1'];
	}

	/**
	 * @param elementValue2 End date if `in_between`, otherwise stubbed as `""`
	 */
	setElementValue2<T2 extends DateSubType = DateSubType>(
		elementValue2: [T2] extends [DateSubType.IN_BETWEEN] ? '' : string,
	) {
		this.dirty = this.elementValue2 !== elementValue2;
		this.elementValue2 = elementValue2 as this['elementValue2'];
	}

	/**
	 * @param elementValue3 User defined attribute name, otherwise stubbed as `""`
	 */
	setElementValue3<T extends DateType = DateType>(
		elementValue3: [T] extends [SystemDefined] ? '' : string,
	) {
		this.elementValue3 = elementValue3 as this['elementValue3'];
	}
}

export default DateRuleModel;
